import React, { useEffect, useState } from 'react';
import {
  Button, IconButton, Panel, useDialogs,
} from '@michelin/acid-components';
import { Grid } from '@material-ui/core';
import { useTranslation } from '@michelin/central-provider';
import DataGrid, {
  Column, FilterRow, Scrolling, SearchPanel,
} from 'devextreme-react/data-grid';
import { getOptionLabel } from '@michelin/select-options-provider';
import { Delete as DeleteIcon, Edit, Visibility } from '@material-ui/icons';
import { cloneDeep, set } from 'lodash';
import uuid from 'uuid';
import FileModalPreview from 'components/FileUpload/FileModalPreview';
import { FileData } from 'components/FileUpload/FileUpload';
import { PlainMessage } from 'components/PlainMessage';
import { Contact, MechanicOutsideCertification } from '../../../Types';
import { MechanicOutsideCertificationsDetail } from './Detail';

interface MechanicOutsideCertificationsListProps {
  contact: Contact,
  readOnly: boolean,
  onChange: (data: MechanicOutsideCertification[]) => void,
}

function MechanicOutsideCertificationsList(props: MechanicOutsideCertificationsListProps) {
  const { readOnly, contact } = props;

  const [certificationsList, setCertificationsList] = useState<MechanicOutsideCertification[]>([]);
  const [certificationDetail, setCertificationDetail] = useState<MechanicOutsideCertification | null>(null);
  const [previewing, setPreviewing] = useState<boolean>(false);
  const [fileData, setFileData] = useState<FileData>({} as FileData);

  useEffect(() => () => {
    setCertificationsList([]);
    setCertificationDetail(null);
  }, []);

  useEffect(() => {
    setCertificationsList(contact.outside_certifications ?? []);
  }, [contact]);

  const { t } = useTranslation();
  const { confirmDialog, errorDialog } = useDialogs();

  /// //////////////////////////////////////////////////////////////////
  /// ACTIONS METHODS
  /// //////////////////////////////////////////////////////////////////

  const isDuplicate = (data: MechanicOutsideCertification): boolean => certificationsList.some((item) => item.type !== 'other' && item.type === data.type && item.id !== data.id);

  const onDetailsClose = () => {
    setCertificationDetail(null);
  };

  const onDetailsSave = (data: MechanicOutsideCertification) => {
    if (isDuplicate(data)) {
      errorDialog(
        t("You've attempted to add a duplicate certification with the following information\n\n\nType: {{certificationType}}", {
          certificationType: getOptionLabel('spp_mechanical_outside_certifications_type', data.type),
        }).split('\n'),
        t('Duplicated certification found'),
      );
    } else {
      const list = cloneDeep(certificationsList).filter((cert: MechanicOutsideCertification) => cert.id !== data.id);
      set(data, 'id', uuid.v4());
      list.push(data);
      setCertificationsList(list);
      onDetailsClose();
      setCertificationDetail(null);
      props.onChange(list);
    }
  };

  const removeCertification = (data: MechanicOutsideCertification) => {
    const list = cloneDeep(certificationsList).filter((cert: MechanicOutsideCertification) => cert.id !== data.id);
    setCertificationsList(list);
    props.onChange(list);
  };

  /// //////////////////////////////////////////////////////////////////
  /// RENDER METHODS
  /// //////////////////////////////////////////////////////////////////

  const renderTypeOfTrainingRow = (data: MechanicOutsideCertification) => {
    if (data.type === 'other') return data.other_type_name;
    return <>{getOptionLabel('spp_mechanical_outside_certifications_type', data.type)}</>;
  };

  const renderProvidedByRow = (data: MechanicOutsideCertification) => data.provided_by;

  const renderDocumentsRow = (data: MechanicOutsideCertification) => {
    if (!data.file) return '';
    return data.file.name;
  };

  const renderActionsRow = (data: MechanicOutsideCertification) => (
    <div style={{ textAlign: 'right' }}>
      {data.file && (
        <IconButton
          aria-label={t('Preview')}
          style={{ margin: '8px' }}
          onClick={() => {
            if (!data.file) return;
            setFileData(data.file);
            setPreviewing(true);
          }}
          size="small"
        >
          <Visibility fontSize="small" />
        </IconButton>
      )}
      {!readOnly && (
        <>
          <IconButton
            aria-label={t('Edit')}
            style={{ margin: '8px' }}
            onClick={() => {
              setCertificationDetail(data);
            }}
            size="small"
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            aria-label={t('Delete')}
            style={{ margin: '8px' }}
            onClick={() => confirmDialog(
              t('Do you want to remove this certification?').split('\n'),
              t('Delete certification'),
              t('Remove certification'),
              t('Keep certification'),
              () => {
                removeCertification(data);
              },
            )}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      )}
    </div>
  );

  return (
    <>
      {(previewing) ? (
        <FileModalPreview
          onClose={() => { setPreviewing(false); }}
          title={t('File preview')}
          fileData={fileData || {} as FileData}
        />
      ) : null}
      <Panel
        title={t('Mechanical Training (Outside Certifications of Training)')}
        control={(
          <span style={{ position: 'relative', top: '4px' }}>
            {readOnly ? null : (
              <Button
                size="small"
                color="primary"
                onClick={() => setCertificationDetail({} as MechanicOutsideCertification)}
              >
                {t('Add')}
              </Button>
            )}
          </span>
              )}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {certificationDetail ? (
              <MechanicOutsideCertificationsDetail
                action={certificationDetail && certificationDetail.id ? 'edit' : 'add'}
                defaultValues={certificationDetail ?? {} as MechanicOutsideCertification}
                onClose={onDetailsClose}
                onSave={onDetailsSave}
              />
            ) : null}
            {(!certificationsList.length
              ? (
                <PlainMessage
                  title={t('Mechanical Training (Outside Certifications of Training) List Empty')}
                  messages={t('There are currently no outside certifications of training for this contact.').split('\n')}
                />
              )
              : (
                <DataGrid
                  dataSource={certificationsList}
                  allowColumnResizing
                >
                  <FilterRow visible={false} />
                  <Scrolling mode="virtual" />
                  <SearchPanel visible={false} highlightCaseSensitive={false} />
                  <Column
                    caption={t('Type of Training')}
                    cellRender={({ row }) => renderTypeOfTrainingRow(row.data)}
                    width={200}
                  />
                  <Column
                    caption={t('Provided By')}
                    cellRender={({ row }) => renderProvidedByRow(row.data)}
                  />
                  <Column
                    caption={t('Certification Documents')}
                    cellRender={({ row }) => renderDocumentsRow(row.data)}
                  />
                  <Column
                    cellRender={({ row }) => renderActionsRow(row.data)}
                    width={(readOnly ? 50 : 150)}
                  />
                </DataGrid>
              ))}
          </Grid>
        </Grid>
      </Panel>
    </>
  );
}

export {
  MechanicOutsideCertificationsList,
};
